import * as React from "react"
import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AlertCircle, Mail, Lock, ArrowRight, Eye, EyeOff, ShieldCheck, RotateCw, KeyRound, Phone } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import ovalLogo from "@/public/oval-logo.svg"

// Casino dark theme color palette
const colors = {
  bg: "#121220",
  surface: "#1C1C32",
  surfaceHover: "#222240",
  inputBg: "#1A1A30",
  border: "#2A2A45",
  borderHover: "#3A3A55",
  borderFocus: "#F37020",
  textPrimary: "#FFFFFF",
  textSecondary: "#9A9ABF",
  textMuted: "#6A6A8A",
  accent: "#F37020",
  accentHover: "#FF8C42",
  error: "#FF4757",
  errorBg: "rgba(255, 71, 87, 0.15)",
  success: "#32CD32",
  successBg: "rgba(50, 205, 50, 0.15)",
  glow: "rgba(243, 112, 32, 0.3)",
}

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
}

// Casino dark input component
function CasinoInput({
  className = "",
  ...props
}: React.ComponentProps<typeof Input>) {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <Input
      {...props}
      className={`
        h-12 text-base font-normal
        rounded-lg transition-all duration-150 ease-out
        placeholder:text-[#6A6A8A]
        ${className}
      `}
      style={{
        backgroundColor: colors.inputBg,
        border: `1px solid ${isFocused ? colors.borderFocus : colors.border}`,
        color: colors.textPrimary,
        boxShadow: isFocused ? `0 0 0 3px ${colors.glow}` : "none",
        ...(props.style || {}),
      }}
      onFocus={(e) => {
        setIsFocused(true)
        props.onFocus?.(e)
      }}
      onBlur={(e) => {
        setIsFocused(false)
        props.onBlur?.(e)
      }}
    />
  )
}

// Casino orange gradient button
function CasinoButton({
  children,
  className = "",
  disabled = false,
  type,
  onClick,
  variant = "primary",
}: {
  children: React.ReactNode
  className?: string
  disabled?: boolean
  type?: "button" | "submit" | "reset"
  onClick?: () => void
  variant?: "primary" | "secondary"
}) {
  const isPrimary = variant === "primary"
  
  return (
    <motion.button
      className={`
        relative px-6 py-3 text-base font-semibold
        rounded-lg transition-all duration-150
        disabled:opacity-40 disabled:cursor-not-allowed
        cursor-pointer overflow-hidden
        ${className}
      `}
      style={{
        background: isPrimary 
          ? `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`
          : "transparent",
        color: isPrimary ? "#FFFFFF" : colors.textPrimary,
        border: isPrimary ? "none" : `1px solid ${colors.border}`,
        boxShadow: isPrimary ? `0 4px 15px ${colors.glow}` : "none",
      }}
      whileHover={disabled ? {} : {
        boxShadow: isPrimary 
          ? `0 6px 20px rgba(243, 112, 32, 0.4)` 
          : `0 0 0 1px ${colors.borderHover}`,
        scale: 1.02,
      }}
      whileTap={disabled ? {} : {
        scale: 0.98,
      }}
      transition={{ duration: 0.1 }}
      disabled={disabled}
      type={type}
      onClick={onClick}
    >
      <span className="relative flex items-center justify-center gap-2">
        {children}
      </span>
    </motion.button>
  )
}

export function AuthPage() {
  const [mode, setMode] = useState<"login" | "signup" | "verify" | "forgot" | "reset">("login")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [verificationCode, setVerificationCode] = useState("")
  const [pendingEmail, setPendingEmail] = useState("")
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState("")
  const [verificationChannel, setVerificationChannel] = useState<"email" | "phone">("email")
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [resetCode, setResetCode] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [forgotEmail, setForgotEmail] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)

  const { login, signup, verifyEmail, verifyPhone, resendVerificationCode, resendPhoneVerificationCode } = useAuth()

  const validateEmail = (email: string) => {
    return /^\S+@\S+\.\S+$/.test(email)
  }

  const validatePhone = (value: string) => {
    const digits = value.replace(/\D/g, "")
    return digits.length >= 10
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!email || !password) {
      setError("Please fill in all fields")
      return
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address")
      return
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters")
      return
    }

    if (mode === "signup" && !phoneNumber) {
      setError("Please enter your phone number")
      return
    }

    if (mode === "signup" && !validatePhone(phoneNumber)) {
      setError("Please enter a valid phone number")
      return
    }

    if (mode === "signup" && password !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    setIsSubmitting(true)

    try {
      const result = mode === "login"
        ? await login(email, password)
        : await signup(email, password, phoneNumber)

      if (result.success && result.requiresPhoneVerification) {
        setPendingEmail(result.email || email)
        setPendingPhoneNumber(result.phoneNumber || phoneNumber)
        setVerificationChannel("phone")
        setVerificationCode("")
        setError("")
        setSuccessMessage("")
        setMode("verify")
        return
      }

      if (result.success && result.requiresVerification) {
        setPendingEmail(result.email || email)
        setVerificationChannel("email")
        setVerificationCode("")
        setError("")
        setSuccessMessage("")
        setMode("verify")
        return
      }

      if (!result.success) {
        setError(result.message || "Something went wrong")
      }
    } catch (err) {
      setError("Network error. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleMode = () => {
    setMode(mode === "login" ? "signup" : "login")
    setError("")
    setSuccessMessage("")
    setPassword("")
    setConfirmPassword("")
    setPhoneNumber("")
    setVerificationCode("")
    setPendingPhoneNumber("")
    setVerificationChannel("email")
    setShowPassword(false)
    setShowConfirmPassword(false)
  }

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccessMessage("")

    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter the 6-digit verification code")
      return
    }

    setIsSubmitting(true)

    try {
      const result = verificationChannel === "email"
        ? await verifyEmail(pendingEmail, verificationCode)
        : await verifyPhone(pendingEmail, verificationCode)

      if (result.success && result.requiresPhoneVerification) {
        setVerificationChannel("phone")
        setPendingPhoneNumber(result.phoneNumber || pendingPhoneNumber)
        setVerificationCode("")
        setSuccessMessage("Email verified. We sent a text message code.")
        return
      }

      if (!result.success) {
        setError(result.message || "Verification failed")
      }
    } catch (err) {
      setError("Network error. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    setError("")
    setSuccessMessage("")
    setIsSubmitting(true)

    try {
      const result = verificationChannel === "email"
        ? await resendVerificationCode(pendingEmail)
        : await resendPhoneVerificationCode(pendingEmail)
      if (result.success) {
        setSuccessMessage(
          verificationChannel === "email"
            ? "New email verification code sent!"
            : "New SMS verification code sent!"
        )
      } else {
        setError(result.message || "Failed to resend code")
      }
    } catch (err) {
      setError("Network error. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ 
        backgroundColor: colors.bg,
        backgroundImage: `radial-gradient(ellipse at top, #1C1C32 0%, ${colors.bg} 70%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="w-full max-w-md relative z-10"
      >
        {/* Main card */}
        <motion.div
          className="rounded-xl overflow-hidden"
          style={{ 
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px ${colors.border}`,
          }}
        >
          {/* Header */}
          <div 
            className="p-6 text-center"
            style={{ borderBottom: `1px solid ${colors.border}` }}
          >
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.2 }}
            >
              <img
                src={ovalLogo}
                alt="Oval"
                className="h-8 mx-auto mb-4"
                style={{ filter: "brightness(1.1)" }}
              />
              <h1 
                className="text-xl font-semibold"
                style={{ color: colors.textPrimary }}
              >
                {mode === "verify"
                  ? verificationChannel === "phone"
                    ? "Verify Your Phone"
                    : "Check Your Email"
                  : mode === "forgot"
                  ? "Forgot Password"
                  : mode === "reset"
                  ? "Reset Password"
                  : mode === "login"
                  ? "Welcome back"
                  : "Create account"}
              </h1>
              <p 
                className="mt-1.5 text-sm"
                style={{ color: colors.textSecondary }}
              >
                {mode === "verify"
                  ? verificationChannel === "phone"
                    ? "Enter the SMS code we sent you"
                    : "Enter the code we sent you"
                  : mode === "forgot"
                  ? "We'll send you a reset code"
                  : mode === "reset"
                  ? "Enter your code and new password"
                  : mode === "login"
                  ? "Sign in to Oval Money Club"
                  : "Start earning rewards today"}
              </p>
            </motion.div>
          </div>

          {/* Form */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              {mode === "forgot" ? (
                <motion.form
                  key="forgot"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.15 }}
                  onSubmit={async (e) => {
                    e.preventDefault()
                    setError("")
                    setSuccessMessage("")
                    if (!forgotEmail) { setError("Please enter your email"); return }
                    setIsSubmitting(true)
                    try {
                      const res = await fetch("/api/auth/forgot-password", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: forgotEmail }),
                      })
                      const data = await res.json()
                      if (data.success) {
                        setSuccessMessage(data.message)
                        setMode("reset")
                      } else {
                        setError(data.message || "Something went wrong")
                      }
                    } catch { setError("Network error") }
                    finally { setIsSubmitting(false) }
                  }}
                  className="space-y-4"
                >
                  {error && (
                    <motion.div 
                      initial={{ opacity: 0, y: -4 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      className="flex items-center gap-2 p-3 rounded-lg text-sm"
                      style={{ backgroundColor: colors.errorBg, color: colors.error }}
                    >
                      <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
                    </motion.div>
                  )}
                  <motion.div className="space-y-1.5" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                    <Label htmlFor="forgotEmail" className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <Mail className="h-4 w-4" style={{ color: colors.textMuted }} />
                      Email Address
                    </Label>
                    <CasinoInput id="forgotEmail" type="email" placeholder="you@example.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="pl-3" autoComplete="email" />
                  </motion.div>
                  <motion.div className="pt-2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <CasinoButton type="submit" disabled={isSubmitting} className="w-full">
                      {isSubmitting ? (<motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />) : (<><KeyRound className="h-4 w-4" />Send Reset Code</>)}
                    </CasinoButton>
                  </motion.div>
                  <motion.div className="text-center pt-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
                    <motion.button type="button" onClick={() => { setMode("login"); setError(""); setSuccessMessage("") }} className="text-sm transition-colors" style={{ color: colors.textSecondary }} whileHover={{ color: colors.textPrimary }} whileTap={{ scale: 0.98 }}>
                      Back to login
                    </motion.button>
                  </motion.div>
                </motion.form>
              ) : mode === "reset" ? (
                <motion.form
                  key="reset"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.15 }}
                  onSubmit={async (e) => {
                    e.preventDefault()
                    setError("")
                    setSuccessMessage("")
                    if (!resetCode || resetCode.length !== 6) { setError("Please enter the 6-digit code"); return }
                    if (!newPassword || newPassword.length < 8) { setError("Password must be at least 8 characters"); return }
                    setIsSubmitting(true)
                    try {
                      const res = await fetch("/api/auth/reset-password", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: forgotEmail, code: resetCode, newPassword }),
                      })
                      const data = await res.json()
                      if (data.success) {
                        setSuccessMessage(data.message)
                        setResetCode("")
                        setNewPassword("")
                        setMode("login")
                      } else {
                        setError(data.message || "Something went wrong")
                      }
                    } catch { setError("Network error") }
                    finally { setIsSubmitting(false) }
                  }}
                  className="space-y-4"
                >
                  {error && (
                    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: colors.errorBg, color: colors.error }}>
                      <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
                    </motion.div>
                  )}
                  {successMessage && (
                    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: colors.successBg, color: colors.success }}>
                      <ShieldCheck className="h-4 w-4 shrink-0" /><span>{successMessage}</span>
                    </motion.div>
                  )}
                  <motion.div className="text-center" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>Enter the code sent to</p>
                    <p className="font-medium mt-0.5" style={{ color: colors.textPrimary }}>{forgotEmail}</p>
                  </motion.div>
                  <motion.div className="space-y-1.5" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Label htmlFor="resetCode" className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <ShieldCheck className="h-4 w-4" style={{ color: colors.textMuted }} />Reset Code
                    </Label>
                    <CasinoInput id="resetCode" type="text" inputMode="numeric" placeholder="000000" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="text-center tracking-[0.3em] text-lg" style={{ letterSpacing: "0.3em" }} maxLength={6} />
                  </motion.div>
                  <motion.div className="space-y-1.5" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                    <Label htmlFor="newPassword" className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <Lock className="h-4 w-4" style={{ color: colors.textMuted }} />New Password
                    </Label>
                    <div className="relative">
                      <CasinoInput id="newPassword" type={showNewPassword ? "text" : "password"} placeholder="Enter new password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="pl-3 pr-10" autoComplete="new-password" />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors cursor-pointer z-10" style={{ color: colors.textMuted }}>
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs ml-0.5" style={{ color: colors.textMuted }}>Must be at least 8 characters</p>
                  </motion.div>
                  <motion.div className="pt-2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <CasinoButton type="submit" disabled={isSubmitting} className="w-full">
                      {isSubmitting ? (<motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />) : "Reset Password"}
                    </CasinoButton>
                  </motion.div>
                  <motion.div className="text-center pt-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>
                    <motion.button type="button" onClick={() => { setMode("login"); setError(""); setSuccessMessage("") }} className="text-sm transition-colors" style={{ color: colors.textSecondary }} whileHover={{ color: colors.textPrimary }} whileTap={{ scale: 0.98 }}>
                      Back to login
                    </motion.button>
                  </motion.div>
                </motion.form>
              ) : mode === "verify" ? (
                <motion.form
                  key="verify"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.15 }}
                  onSubmit={handleVerifySubmit}
                  className="space-y-4"
                >
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-3 rounded-lg text-sm"
                      style={{ backgroundColor: colors.errorBg, color: colors.error }}
                    >
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )}

                  {successMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-3 rounded-lg text-sm"
                      style={{ backgroundColor: colors.successBg, color: colors.success }}
                    >
                      <ShieldCheck className="h-4 w-4 shrink-0" />
                      <span>{successMessage}</span>
                    </motion.div>
                  )}

                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <p className="text-sm" style={{ color: colors.textSecondary }}>
                      {verificationChannel === "phone" ? "We sent a text message code to" : "We sent a code to"}
                    </p>
                    <p className="font-medium mt-0.5" style={{ color: colors.textPrimary }}>
                      {verificationChannel === "phone" ? pendingPhoneNumber : pendingEmail}
                    </p>
                  </motion.div>

                  <motion.div
                    className="space-y-1.5"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Label htmlFor="code" className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      {verificationChannel === "phone" ? (
                        <Phone className="h-4 w-4" style={{ color: colors.textMuted }} />
                      ) : (
                        <ShieldCheck className="h-4 w-4" style={{ color: colors.textMuted }} />
                      )}
                      {verificationChannel === "phone" ? "SMS Code" : "Verification Code"}
                    </Label>
                    <CasinoInput
                      id="code"
                      type="text"
                      inputMode="numeric"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="text-center text-lg"
                      style={{ letterSpacing: "0.3em" }}
                      maxLength={6}
                      autoComplete="one-time-code"
                    />
                  </motion.div>

                  <motion.div
                    className="pt-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <CasinoButton
                      type="submit"
                      disabled={isSubmitting || verificationCode.length !== 6}
                      className="w-full"
                    >
                      {isSubmitting ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                        />
                      ) : (
                        verificationChannel === "phone" ? "Verify Phone" : "Verify Email"
                      )}
                    </CasinoButton>
                  </motion.div>

                  <motion.div
                    className="text-center pt-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <p className="text-sm" style={{ color: colors.textSecondary }}>
                      {verificationChannel === "phone" ? "Didn't receive the text?" : "Didn't receive the code?"}{" "}
                      <motion.button
                        type="button"
                        onClick={handleResendCode}
                        disabled={isSubmitting}
                        className="font-medium disabled:opacity-50 inline-flex items-center gap-1"
                        style={{ color: colors.accent }}
                        whileHover={{ color: colors.accentHover }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <RotateCw className="h-3.5 w-3.5" />
                        Resend
                      </motion.button>
                    </p>
                  </motion.div>

                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.25 }}
                  >
                    <motion.button
                      type="button"
                      onClick={() => {
                        setMode("login")
                        setError("")
                        setSuccessMessage("")
                      }}
                      className="text-sm transition-colors"
                      style={{ color: colors.textSecondary }}
                      whileHover={{ color: colors.textPrimary }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Back to login
                    </motion.button>
                  </motion.div>
                </motion.form>
              ) : (
                <motion.form
                  key={mode}
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.15 }}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-3 rounded-lg text-sm"
                      style={{ backgroundColor: colors.errorBg, color: colors.error }}
                    >
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )}

                  <motion.div
                    className="space-y-1.5"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <Mail className="h-4 w-4" style={{ color: colors.textMuted }} />
                      Email
                    </Label>
                    <CasinoInput
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-3"
                      autoComplete="email"
                      autoCapitalize="none"
                      spellCheck="false"
                    />
                  </motion.div>

                  <AnimatePresence>
                    {mode === "signup" && (
                      <motion.div
                        className="space-y-1.5"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Label htmlFor="phoneNumber" className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
                          <Phone className="h-4 w-4" style={{ color: colors.textMuted }} />
                          Phone Number
                        </Label>
                        <CasinoInput
                          id="phoneNumber"
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          className="pl-3"
                          autoComplete="tel"
                        />
                        <p className="text-xs ml-0.5" style={{ color: colors.textMuted }}>
                          We&apos;ll text a 6-digit signup verification code
                        </p>
                        <p className="text-xs ml-0.5 mt-2 leading-relaxed" style={{ color: colors.textMuted }}>
                          By providing your phone number, you agree to receive promotional text messages from Oval Money Club.
                          Message frequency varies. Message and data rates may apply.
                          Reply STOP to opt out at any time.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.div
                    className="space-y-1.5"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Label htmlFor="password" className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
                      <Lock className="h-4 w-4" style={{ color: colors.textMuted }} />
                      Password
                    </Label>
                    <div className="relative">
                      <CasinoInput
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-3 pr-10"
                        autoComplete={mode === "login" ? "current-password" : "new-password"}
                      />
                      {mode === "signup" && (
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors cursor-pointer z-10"
                          style={{ color: colors.textMuted }}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                    {mode === "signup" && (
                      <p className="text-xs ml-0.5" style={{ color: colors.textMuted }}>
                        Must be at least 8 characters
                      </p>
                    )}
                  </motion.div>

                  <AnimatePresence>
                    {mode === "signup" && (
                      <motion.div
                        className="space-y-1.5"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Label htmlFor="confirmPassword" className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
                          <Lock className="h-4 w-4" style={{ color: colors.success }} />
                          Confirm Password
                        </Label>
                        <div className="relative">
                          <CasinoInput
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="pl-3 pr-10"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors cursor-pointer z-10"
                            style={{ color: colors.textMuted }}
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {mode === "login" && (
                    <motion.div
                      className="text-right -mt-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.12 }}
                    >
                      <motion.button
                        type="button"
                        onClick={() => {
                          setForgotEmail(email)
                          setError("")
                          setSuccessMessage("")
                          setMode("forgot")
                        }}
                        className="text-sm transition-colors"
                        style={{ color: colors.accent }}
                        whileHover={{ color: colors.accentHover }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Forgot password?
                      </motion.button>
                    </motion.div>
                  )}

                  <motion.div
                    className="pt-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <CasinoButton
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full"
                    >
                      {isSubmitting ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                        />
                      ) : (
                        <>
                          {mode === "login" ? "Sign In" : "Create Account"}
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </CasinoButton>
                  </motion.div>

                  <motion.div
                    className="text-center pt-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <p className="text-sm" style={{ color: colors.textSecondary }}>
                      {mode === "login" ? "New to the club?" : "Already a member?"}{" "}
                      <motion.button
                        type="button"
                        onClick={toggleMode}
                        className="font-medium"
                        style={{ color: colors.accent }}
                        whileHover={{ color: colors.accentHover }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {mode === "login" ? "Join Now" : "Sign In"}
                      </motion.button>
                    </p>
                  </motion.div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Bottom tagline */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.2 }}
          className="mt-5 text-center"
        >
          <div 
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{ 
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
            }}
          >
            <span className="text-xs flex items-center gap-1.5" style={{ color: colors.textMuted }}>
              Powered by
              <img src={ovalLogo} alt="Oval" className="h-3 inline-block opacity-80" />
            </span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}
